import { createContext, useState, useEffect, type ReactNode } from 'react';
import type { Language } from './translations';
import { useAuthStore } from '../auth/authStore';
import { authApi } from '../auth/authApi';

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
}

export const LanguageContext = createContext<LanguageContextValue>({
  language: 'en',
  setLanguage: () => {},
});

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'darkage_language';

function readStoredLanguage(): Language {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'pt' || v === 'en') return v;
  } catch (err) {
    // localStorage may be unavailable in some environments (private mode, etc.)
    console.warn('[i18n] Could not read language from localStorage:', err);
  }
  return 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(readStoredLanguage);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch (err) {
      console.warn('[i18n] Could not persist language to localStorage:', err);
    }
    
    // Also save it to the backend if the user is authenticated
    const token = useAuthStore.getState().token;
    if (token) {
      authApi.updateMe(token, { language: lang }).catch(() => {
        // silently ignore failures here
      });
    }
  };

  // Keep language in sync with the authenticated user data (hydrate/login)
  useEffect(() => {
    const unsubscribe = useAuthStore.subscribe((state, prevState) => {
      // If we just logged in or hydrated, and the user has a language, apply it
      if (state.user && state.user.language && (!prevState.user || prevState.user.language !== state.user.language)) {
        if (state.user.language === 'pt' || state.user.language === 'en') {
          setLanguageState(state.user.language as Language);
          try {
            localStorage.setItem(STORAGE_KEY, state.user.language);
          } catch (err) {}
        }
      }
    });
    return unsubscribe;
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}
